import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import {
  type ShapeTextFlow,
  type ShapeTextLayoutOptions,
  type ShapeTextLayoutResult,
  type ShapeTextRow,
  layoutShapeTextContent,
} from '@/lib/shapes/shape-text'
import type { DropCapStyle } from '@/lib/text/types/drop-cap-style'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { DropCapBox } from './types/drop-cap-box'
import type { DropCapLayoutResult } from './types/drop-cap-layout-result'
import { computeDropCapPlacements, solveDropCapFits } from './helpers/drop-cap-placements'

/** Narrows a row's leftmost segment by `indentPt`, dropping it entirely if the indent consumes the
 *  whole segment (a narrow/concave row) — matching how a too-narrow segment elsewhere in this
 *  engine is simply absent rather than an inverted `[startX > endX]` range. */
function narrowRowLeftEdge(row: ShapeTextRow, indentPt: number): ShapeTextRow {
  if (indentPt <= 0 || row.segments.length === 0) return row

  const [first, ...rest] = row.segments
  const narrowedStartX = first.startX + indentPt
  const narrowedFirst =
    narrowedStartX < first.endX ? { startX: narrowedStartX, endX: first.endX } : null

  return { ...row, segments: narrowedFirst ? [narrowedFirst, ...rest] : rest }
}

/** Applies a `{ absolute row index in flow.rows -> indentPt }` map to `flow`. */
function narrowFlowRows(
  flow: ShapeTextFlow,
  rowIndentByIndex: ReadonlyMap<number, number>
): ShapeTextFlow {
  if (rowIndentByIndex.size === 0) return flow

  return {
    ...flow,
    rows: flow.rows.map((row, index) => {
      const indentPt = rowIndentByIndex.get(index)

      return indentPt == null ? row : narrowRowLeftEdge(row, indentPt)
    }),
  }
}

/** Whether two `{ row index -> indentPt }` maps agree on every entry — the fixed-point test
 *  `layoutShapeTextWithDropCap`'s convergence loop stops on. */
function rowIndentMapsEqual(
  a: ReadonlyMap<number, number>,
  b: ReadonlyMap<number, number>
): boolean {
  if (a.size !== b.size) return false

  for (const [rowIndex, indentPt] of a) {
    if (b.get(rowIndex) !== indentPt) return false
  }

  return true
}

/**
 * Distinct row indices (positions in `flow.rows`) that need geometric narrowing for one placement,
 * from `layout.chunks` — the same "chunks arrive in flow order, yTop only ever increases" fact
 * `bake-shape-text-wire-lines.ts`'s `groupChunksIntoRows` already relies on.
 *
 * **Inside** excludes the paragraph's own first (cap) row — it's indented via the cap's forced
 * advance, not segment narrowing — and caps the rest at `lines - 1` further rows: Inside never
 * indents past the paragraph's own `lines`'th row regardless of how many more rows the paragraph's
 * text goes on to fill.
 *
 * **Outside** narrows *every* row the paragraph occupies, row 0 included — the cap consumes no
 * advance of its own (it hangs outside the margin, not inline), so row 0 has nothing else to
 * reserve its space and is narrowed exactly like the rest. `lines` plays no role here: Outside's
 * indent applies uniformly regardless of the paragraph's own length.
 */
function occupiedRowIndicesToNarrow(
  layout: ShapeTextLayoutResult,
  flow: ShapeTextFlow,
  charIndex: number,
  paragraphEndIndex: number,
  style: DropCapStyle,
  lines: number
): number[] {
  const yTopsInOrder: number[] = []
  const seen = new Set<number>()

  for (const chunk of layout.chunks) {
    if (chunk.startCharIndex < charIndex || chunk.startCharIndex >= paragraphEndIndex) continue
    if (seen.has(chunk.yTop)) continue
    seen.add(chunk.yTop)
    yTopsInOrder.push(chunk.yTop)
  }

  const scoped = style === 'INS' ? yTopsInOrder.slice(1, lines) : yTopsInOrder

  return scoped
    .map((yTop) => flow.rows.findIndex((row) => row.yTop === yTop))
    .filter((index) => index >= 0)
}

/**
 * The drop-cap-aware counterpart to `layoutShapeTextContent`: identical to a plain call when no
 * paragraph has an active drop cap (falls straight through, zero behavior change), and otherwise
 * fits each cap's size, converges the row-narrowing those caps need, and reports where to paint
 * each one.
 *
 * **Inside vs Outside differ in which rows narrow and how the cap consumes its own row — NOT in
 * vertical position, which is identical for both.** Both share one `indentPt` formula
 * (`solveDropCapFits`) and one vertical anchor: ink-top pinned to the top of the body text on row
 * 0, baseline falling `lines` rows down (see the capBoxes loop below, and `inkHeightPt`'s own doc
 * comment). Inside reserves its row 0 via `ShapeTextLayoutOptions.dropCapOverrides`' forced
 * advance — the glyph sits inline, so that row never needs geometric narrowing — and narrows only
 * its following `lines - 1` rows, which have no glyph of their own to reserve the space. Outside
 * instead gives the cap a forced advance of *zero* (it consumes no inline space at all — it hangs
 * outside the margin) and narrows *every* row the paragraph occupies, row 0 included, uncapped by
 * `lines`. `occupiedRowIndicesToNarrow` is where that branch lives — the only one left; an earlier
 * version also gave Outside a different vertical anchor (baseline pinned to row 0's raw top,
 * glyph rising above it), which regressed for a drop cap on a frame's very first row: `rowTopY` is
 * ~0 there, so subtracting the cap's own ascent drove the glyph's top negative and off-canvas.
 *
 * **Why this converges by rebuilding the narrowing map from scratch every pass, not by
 * accumulating into one that only grows.** *Which* rows a placement occupies can only be
 * discovered by running the real layout (paragraph boundaries fall wherever earlier content
 * wrapped to), and narrowing a row can itself push text into an additional row that also needs
 * narrowing — so this re-runs until a pass reproduces the exact map it was given. An earlier
 * version accumulated discoveries into one map that only ever grew, on the assumption that a
 * placement's own occupied-row set only ever grows across passes. That assumption fails across
 * placements: narrowing an EARLIER paragraph's rows can grow ITS OWN row count, which pushes every
 * LATER paragraph's cap onto a different absolute row — so a later paragraph's row assignment from
 * an early pass can go stale once an earlier paragraph's narrowing is fully accounted for, and a
 * map that never removes a stale entry keeps narrowing a row that placement no longer starts on
 * while missing the row it actually moved to (WMPCM drop-cap wrapping bug: a second paragraph's own
 * indent looked like it randomly skipped a row and picked back up one row later). Bounded at
 * `flow.rows.length` passes: the whole flow has no more rows than that to ever disagree over.
 */
export function layoutShapeTextWithDropCap(
  text: string,
  flow: ShapeTextFlow,
  measureTextPt: (value: string, charIndex?: number) => number,
  paragraphs: Paragraph[],
  charRunMap: Array<TextRun | null>,
  ctx: CanvasRenderingContext2D | null,
  fonts: ParagraphBlockFontLookup | undefined,
  paragraphAlignments?: number[],
  options?: ShapeTextLayoutOptions
): DropCapLayoutResult {
  const placements = computeDropCapPlacements(paragraphs, text, charRunMap)

  if (placements.length === 0) {
    return {
      layout: layoutShapeTextContent(text, flow, measureTextPt, paragraphAlignments, options),
      flow,
      capBoxes: [],
    }
  }

  const fits = solveDropCapFits(placements, ctx, fonts)
  // Inside reserves the cap's own row via a forced inline advance; Outside gives it zero advance —
  // it hangs outside the margin and consumes no space in the flow at all.
  const dropCapOverrides = fits.map((fit) => ({
    charIndex: fit.charIndex,
    advancePt: fit.style === 'INS' ? fit.indentPt : 0,
  }))
  let rowIndentByIndex = new Map<number, number>()

  let layout = layoutShapeTextContent(text, flow, measureTextPt, paragraphAlignments, {
    ...options,
    dropCapOverrides,
  })

  const maxIterations = Math.max(1, flow.rows.length)

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const nextRowIndentByIndex = new Map<number, number>()

    for (const fit of fits) {
      const rowIndices = occupiedRowIndicesToNarrow(
        layout,
        flow,
        fit.charIndex,
        fit.paragraphEndIndex,
        fit.style,
        fit.lines
      )

      for (const rowIndex of rowIndices) {
        nextRowIndentByIndex.set(rowIndex, fit.indentPt)
      }
    }

    if (rowIndentMapsEqual(nextRowIndentByIndex, rowIndentByIndex)) break

    rowIndentByIndex = nextRowIndentByIndex

    const narrowedFlow = narrowFlowRows(flow, rowIndentByIndex)

    layout = layoutShapeTextContent(text, narrowedFlow, measureTextPt, paragraphAlignments, {
      ...options,
      dropCapOverrides,
    })
  }

  const finalFlow = narrowFlowRows(flow, rowIndentByIndex)
  const capBoxes: DropCapBox[] = fits.map((fit) => {
    const capChunk = layout.chunks.find((chunk) => chunk.startCharIndex === fit.charIndex)
    const rowLeftX = capChunk?.x ?? flow.rows[0]?.segments[0]?.startX ?? 0
    const rowTopY = capChunk?.yTop ?? flow.rows[0]?.yTop ?? 0

    // This placement's OWN row-top-to-baseline offset, matching the boundary's own convention
    // (`shape-text.go`'s `BaselineY: yTop + rowHeight*0.8`) but applied to `ownRowHeightPt` — the
    // paragraph's own pitch, not the frame-wide probe (see that field's doc comment).
    const baselineOffsetPt = fit.ownRowHeightPt * 0.8
    // Top of the body text on the cap's own row (the row's own baseline minus its ascent) —
    // exactly the reference `bodyAscentPt` was measured against in `solveDropCapFits`, and the
    // SAME reference for both styles: `solveDropCapFits`'s own doc comment already establishes
    // "sizing is identical for both" (same `inkHeightPt`, same vertical span), so both styles'
    // ink-top land here too — Inside descends `lines` rows from it, Outside is identical in this
    // dimension and differs only in indent scope/side (see `x` and `occupiedRowIndicesToNarrow`).
    // A style-specific baseline-anchor for Outside (pinning it to the row's raw top instead) was
    // tried previously and regressed: for a drop cap on the FIRST row of a frame, `rowTopY` is
    // ~0, so subtracting the cap's own (large, solved) ascent from it drove the glyph's top
    // negative — rendered above the frame entirely, invisible.
    const bodyTextTopY = rowTopY + baselineOffsetPt - fit.bodyAscentPt

    // The div renders at `line-height: normal`, where the glyph's ink-top sits `capTopInsetPt`
    // below the CSS `top` — so subtracting it back out is what lands the ink exactly at
    // `bodyTextTopY`.
    const divTopY = bodyTextTopY - fit.capTopInsetPt

    return {
      paragraphIndex: fit.paragraphIndex,
      charIndex: fit.charIndex,
      // Outside's row was narrowed by the full indent same as any other row of that paragraph, so
      // its emitted (zero-advance) chunk sits at the NEW margin — subtract the indent back out to
      // hang the glyph to the left of it, in the space the indent reserved.
      x: fit.style === 'INS' ? rowLeftX : rowLeftX - fit.indentPt,
      yTop: divTopY,
      rowTopY,
      fontSizePt: fit.fontSizePt,
      character: fit.character,
      fontFamilyId: fit.fontFamilyId,
      fontId: fit.fontId,
      heightPt: fit.inkHeightPt,
    }
  })

  return { layout, flow: finalFlow, capBoxes }
}
